import axios from 'axios';
import { notifications } from '@shared/utils/notifications';
import { sessionExpired } from '@mobx/root';
import { CORE_PATH } from '@shared/api/paths';
import { getSessionToken, hasSessionToken } from '@shared/api/session';
import { PATH_HOME } from '@/app/routes/paths';

const apiUrl = process.env.VITE_REACT_APP_API_URL;

export const instance = axios.create({
  baseURL: apiUrl,
  timeout: 60000,
  headers: { 'Content-Type': 'application/json; charset=utf-8' }
});

/**
 * Таймаут для запросов отправки подписи (CMS от NCALayer) в oci-docs: подписанный АВР и договор.
 * oci-docs ставит подпись платформы с меткой времени TSA НУЦ (tsp.pki.gov.kz), подписания
 * сериализуются глобальным мьютексом (до 3 попыток с паузами 2 с), а на проде oci-docs ждёт
 * Core до 120 с (OCI_CORE_TIMEOUT) — стандартных 60 с из `instance` не хватает.
 * Применяется только к запросам отправки подписи, а не ко всем запросам.
 */
export const SIGN_REQUEST_TIMEOUT = 180_000;

/**
 * OSI-845: таймаут для формирования квитанций «отдельными файлами» (separateFiles=true).
 * Сервер собирает ZIP из PDF по каждой квартире — на крупных ОСИ это может длиться минуты,
 * стандартных 60 с не хватает. Применяется только к запросам с separateFiles=true.
 */
export const SEPARATE_INVOICES_REQUEST_TIMEOUT = 600_000;

instance.interceptors.request.use((config) => {
  if (config.url?.indexOf(`${CORE_PATH}/Auth`) !== 0) {
    const token = getSessionToken();

    // OSI-796: не отправляем пустой «Bearer » при отсутствии токена (см. shared/api/session.ts).
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
  }
  return config;
});

instance.interceptors.response.use(
  (response) => {
    const { data } = response;
    const { code, message, result } = data;
    if (!code && !message && !result) {
      return data;
    }
    if (code !== 0) throw message;

    try {
      if (response.config.url?.indexOf(`${CORE_PATH}/Auth`) === 0 && result && result.token) {
        localStorage.setItem('token', result.token);
      }
    } catch (err) {
      console.error(err);
    }
    return result;
  },
  (error) => {
    if (error?.response?.status === 401) {
      const isHomePage = window.location.pathname === PATH_HOME.root;
      // OSI-796: к существующему guard'у по главной добавлена проверка наличия токена —
      // без неё неавторизованный пользователь на /auth/login видел ложную «Сессия истекла»
      // (справочник инструкций KeyValues грузится из RootLayout на любой странице).
      if (!isHomePage && hasSessionToken()) {
        notifications.warning('Сессия истекла');
      }

      // Оркестратор: authModule.sessionExpired() + очистка persist всех сессионных сторов.
      sessionExpired();

      return;
    }
    if (error?.response?.status === 400 && error?.response?.data?.errors) {
      error.message = Object.entries(error.response.data.errors).reduce(
        (acc, [key, value], index) =>
          `${acc}${index > 0 ? ';' : ''}${key}: ${Array.isArray(value) ? value.join(',') : JSON.stringify(value)}`,
        error.response.data?.title || ''
      );
    }

    // 413 режет внешний nginx (client_max_body_size) ДО приложения — тело ответа HTML,
    // а не ApiResponse, поэтому error.message остаётся сырым "Request failed with status code 413".
    // Подставляем i18n-КЛЮЧ, чтобы пользователь увидел причину, а не пустой тост «Ошибка».
    if (error?.response?.status === 413) {
      error.message = 'common:fileTooLargeServer';
    }
    throw error;
  }
);
